import React, { useState, useContext } from 'react';
import moment from 'moment';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Paper,
  Grid,
  Button,
  Typography,
  Box
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { Search } from '@mui/icons-material';
import { formatURLTitle } from '../../../helpers/http';
import { articleQueries } from '../../../graphql/queries';
import MoreMenuButton from '../../../Components/MoreMenuButton';
import ArticleMenu from './ArticleMenu';
import SearchChip from '../../../Components/SearchChip';
import SavingBackdrop from '../../../Components/Forms/SavingBackdrop';
import { UserContext } from '../../../Context';

// Sortable columns map to Article model attributes; generateOrderBy on the server
// resolves these to the underlying columns (publish_date_time etc.).
const SORT_COLUMNS = [
  { id: 'title', label: 'Title' },
  { id: 'status', label: 'Status' },
  { id: 'publishDateTime', label: 'Publish date' }
];

// Article table
function ArticleTable ({
  rows,
  subscriptionId,
  subscriptionTitle,
  orderBy,
  orderByDirection,
  onSort,
  refetchArticles
}) {
  const [menuOpen, setMenOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const { user } = useContext(UserContext);

  const getArticleLink = (row) => {
    return `/admin/subscriptions/${subscriptionId}/${formatURLTitle(subscriptionTitle)}/articles/${row.id}/${formatURLTitle(row.title)}`;
  };

  const handleMoreMenuClick = row => (event) => {
    setSelectedArticle(row);
    setAnchorEl(event.currentTarget);
    setMenOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenOpen(false);
    setSelectedArticle(null);
  };

  return (
    <TableContainer component={Paper}>
      <Table style={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            {SORT_COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                sortDirection={orderBy === column.id ? orderByDirection : false}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? orderByDirection : 'desc'}
                  onClick={() => onSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Free</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              hover
              key={row.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>
                {
                  user.type !== 'Publisher' || row.status === 'Draft'
                    ? (
                      <Link to={getArticleLink(row)}>{row.title}</Link>
                      )
                    : <>{row.title}</>
                }
              </TableCell>
              <TableCell>{row.status}</TableCell>
              <TableCell>{moment(row.publishDateTime).format('MMMM Do YYYY')}</TableCell>
              <TableCell>{row.freeToRead && <DoneIcon />}</TableCell>
              <TableCell>
                <MoreMenuButton onClick={handleMoreMenuClick(row)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ArticleMenu
        anchorEl={anchorEl}
        article={selectedArticle}
        subscriptionId={subscriptionId}
        open={menuOpen}
        onClose={handleMenuClose}
        refetchSubscriptions={refetchArticles}
      />
    </TableContainer>
  );
}

export default function Articles ({ subscription }) {
  const navigate = useNavigate();

  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  // Date-desc default: the admin table opens newest-first, served via the
  // (product_id, publish_date_time) composite index added in Phase A.
  const [orderBy, setOrderBy] = useState('publishDateTime');
  const [orderByDirection, setOrderByDirection] = useState('desc');

  const { data, refetch, loading } = useQuery(
    articleQueries.ArticleSearchAdmin({
      subscriptionId: subscription.id,
      searchTerm,
      limit,
      offset: page * limit,
      orderBy,
      orderByDirection
    }),
    { fetchPolicy: 'no-cache' }
  );

  const result = data && data.articleSearchAdmin;
  const rows = result ? result.articles : [];
  const count = result ? result.count : 0;

  const handleSort = (column) => {
    const isAsc = orderBy === column && orderByDirection === 'desc';
    setOrderBy(column);
    setOrderByDirection(isAsc ? 'asc' : 'desc');
    setPage(0);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(localSearchTerm);
    setPage(0);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    setSearchTerm('');
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddClick = () => {
    navigate(`/admin/subscriptions/${subscription.id}/${subscription.title}/articles/add`);
  };

  return (
    <Paper style={{ padding: 20 }}>
      <SavingBackdrop open={loading} text='Loading...' />
      <Grid
        container
        rowSpacing={2}
        alignItems='flex-start'
        justifyContent='space-between'
        flexDirection='column'
      >
        <Grid item style={{ width: '100%' }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='h5' gutterBottom>
              Articles
            </Typography>
            <Button
              onClick={handleAddClick}
              variant='contained'
            >
              Add Article
            </Button>
          </Box>
        </Grid>
        <Grid item style={{ width: '100%' }}>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              fullWidth
              variant='standard'
              placeholder='Search articles by title'
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search color='action' />
                  </InputAdornment>
                )
              }}
            />
          </form>
        </Grid>
        {searchTerm && (
          <Grid item>
            <SearchChip
              active
              label={`${count} search result${count === 1 ? '' : 's'}`}
              name='articleSearch'
              onDelete={handleClearSearch}
            />
          </Grid>
        )}
        <Grid item style={{ width: '100%' }}>
          <ArticleTable
            rows={rows}
            subscriptionId={subscription.id}
            subscriptionTitle={subscription.title}
            orderBy={orderBy}
            orderByDirection={orderByDirection}
            onSort={handleSort}
            refetchArticles={refetch}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component='div'
            count={count}
            rowsPerPage={limit}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
